import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { Trash2, RotateCcw, AlertTriangle, Info, ArrowLeft } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RETENTION_DAYS_FALLBACK = 90;

function daysBetween(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    const diff = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
    return Math.floor(diff);
  } catch {
    return null;
  }
}

export default function TrashOperations() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retentionDays, setRetentionDays] = useState(RETENTION_DAYS_FALLBACK);
  const [selected, setSelected] = useState([]);

  const isAdmin = user?.role?.toLowerCase() === 'admin';

  const fetchTrash = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/transport-requests?only_deleted=true&limit=500`);
      const list = Array.isArray(res.data) ? res.data : (res.data?.operations || res.data?.data || []);
      setOperations(list);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err?.response?.data?.detail || 'Impossible de charger la corbeille',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchRetention = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/system-config`);
      const val = res?.data?.system?.trash_retention_days;
      if (typeof val === 'number' && val > 0) setRetentionDays(val);
    } catch {
      /* fallback to default */
    }
  }, []);

  useEffect(() => {
    fetchTrash();
    fetchRetention();
  }, [fetchTrash, fetchRetention]);

  const toggleSelected = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleAll = () => {
    setSelected((prev) => (prev.length === operations.length ? [] : operations.map((o) => o.id)));
  };
  const clearSelected = () => setSelected([]);

  const handleRestoreOne = async (id) => {
    if (!window.confirm('Restaurer cette opération depuis la corbeille ?')) return;
    try {
      await axios.post(`${API}/transport-requests/${id}/restore-from-trash`);
      toast({ title: 'Opération restaurée', description: 'Elle est de nouveau visible dans la liste' });
      fetchTrash();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Restauration impossible', variant: 'destructive' });
    }
  };

  const handlePurgeOne = async (id, ref) => {
    if (!isAdmin) return;
    if (!window.confirm(`SUPPRIMER DÉFINITIVEMENT l'opération ${ref || id} ? Cette action est irréversible et ne pourra PAS être annulée.`)) return;
    try {
      await axios.delete(`${API}/transport-requests/${id}/purge`);
      toast({ title: 'Opération purgée', description: 'Suppression définitive effectuée' });
      fetchTrash();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Purge impossible', variant: 'destructive' });
    }
  };

  const handleBulkRestore = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Restaurer ${selected.length} opération(s) depuis la corbeille ?`)) return;
    try {
      const res = await axios.post(`${API}/bulk-actions/restore-from-trash`, { operationIds: selected });
      toast({ title: 'Restauration effectuée', description: `${res.data.success_count} opération(s) restaurée(s)` });
      if (res.data.failed_count > 0) {
        toast({ title: 'Certaines restaurations ont échoué', description: `${res.data.failed_count} échec(s)`, variant: 'destructive' });
      }
      clearSelected();
      fetchTrash();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Restauration en masse impossible', variant: 'destructive' });
    }
  };

  if (!['admin', 'lead_ops', 'lead_commercial', 'auditeur'].includes((user?.role || '').toLowerCase())) {
    return (
      <Card className="p-6 m-6">
        <div className="flex items-center gap-3 text-red-600">
          <AlertTriangle className="w-5 h-5" />
          <span>Accès réservé aux rôles admin, lead_ops, lead_commercial et auditeur.</span>
        </div>
      </Card>
    );
  }

  return (
    <div className="p-6 space-y-4" data-testid="trash-operations-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/operations')}
            data-testid="trash-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Trash2 className="w-6 h-6 text-red-500" />
              Corbeille
            </h1>
            <p className="text-sm text-gray-500">
              Opérations supprimées récupérables. Purge automatique après {retentionDays} jour(s).
            </p>
          </div>
        </div>
        <Badge variant="outline" className="text-sm">{operations.length} opération(s)</Badge>
      </div>

      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex gap-3 items-start text-sm text-blue-900">
          <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            Une opération supprimée reste dans la corbeille pendant <strong>{retentionDays} jours</strong> avant
            d'être purgée définitivement. Vous pouvez la restaurer (elle retrouvera son statut précédent) ou la
            supprimer définitivement (admin uniquement).
          </div>
        </div>
      </Card>

      {selected.length > 0 && (
        <Card className="p-3 bg-emerald-50 border-emerald-300 flex items-center justify-between">
          <span className="text-sm text-emerald-800 font-semibold">{selected.length} sélectionnée(s)</span>
          <div className="flex gap-2">
            {isAdmin && (
              <Button
                size="sm"
                onClick={handleBulkRestore}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="trash-bulk-restore-btn"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Restaurer {selected.length} opération(s)
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={clearSelected}>Annuler la sélection</Button>
          </div>
        </Card>
      )}

      <Card className="overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Chargement…</div>
        ) : operations.length === 0 ? (
          <div className="p-12 text-center">
            <Trash2 className="w-12 h-12 mx-auto text-gray-300 mb-2" />
            <p className="text-gray-500">La corbeille est vide.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={selected.length === operations.length && operations.length > 0}
                    onCheckedChange={toggleAll}
                    data-testid="trash-select-all"
                  />
                </TableHead>
                <TableHead>Référence</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Statut au moment</TableHead>
                <TableHead>Supprimée par</TableHead>
                <TableHead>Date suppression</TableHead>
                <TableHead className="text-right">Jours restants</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {operations.map((op) => {
                const ageDays = daysBetween(op.deletedAt);
                const daysLeft = ageDays != null ? Math.max(0, retentionDays - ageDays) : null;
                const urgent = daysLeft != null && daysLeft <= 7;
                return (
                  <TableRow key={op.id} data-testid={`trash-row-${op.id}`}>
                    <TableCell>
                      <Checkbox
                        checked={selected.includes(op.id)}
                        onCheckedChange={() => toggleSelected(op.id)}
                        data-testid={`trash-select-${op.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-sm">{op.operationNumber || op.id?.slice(0, 8)}</TableCell>
                    <TableCell>{op.companyName || op.userName || '—'}</TableCell>
                    <TableCell><Badge variant="outline">{op.status || '—'}</Badge></TableCell>
                    <TableCell>{op.deletedBy || '—'}</TableCell>
                    <TableCell>
                      {op.deletedAt ? new Date(op.deletedAt).toLocaleDateString('fr-FR') : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {daysLeft != null ? (
                        <Badge className={urgent ? 'bg-red-100 text-red-700 border-red-300' : 'bg-gray-100 text-gray-700'}>
                          {daysLeft}j
                        </Badge>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      {isAdmin && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-emerald-600 hover:bg-emerald-50"
                          onClick={() => handleRestoreOne(op.id)}
                          title="Restaurer"
                          data-testid={`trash-restore-${op.id}`}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      )}
                      {isAdmin && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handlePurgeOne(op.id, op.operationNumber)}
                          title="Supprimer définitivement"
                          data-testid={`trash-purge-${op.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
